import React, { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import CapacitorAuthService from '../utils/capacitorAuth';
import PermissionsManager from '../utils/permissionsManager';
import { toast } from 'react-hot-toast';
import PermissionRequestDialog from './PermissionRequestDialog';
import ContactSyncSettings from './ContactSyncSettings';
import { auth } from '../firebase.config';

const MobileSettings = () => {
  const [permissions, setPermissions] = useState({});
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [permissionDialog, setPermissionDialog] = useState({
    isOpen: false,
    permission: null,
    isRequesting: false
  });

  useEffect(() => {
    loadMobileSettings();
  }, []);

  const loadMobileSettings = async () => {
    try {
      setIsLoading(true);

      // Check if we're on mobile
      if (!Capacitor.isNativePlatform()) {
        setIsLoading(false);
        return;
      }

      // Load device info
      const deviceInfo = CapacitorAuthService.getDeviceInfo();
      setDeviceInfo(deviceInfo);

      // Check biometric availability with error handling
      try {
        const biometricAvailable = await CapacitorAuthService.safeCheckBiometricAvailability();
        setBiometricAvailable(biometricAvailable);

        if (biometricAvailable) {
          const biometricEnabled = await CapacitorAuthService.isBiometricEnabled();
          setBiometricEnabled(biometricEnabled);
        }
      } catch (error) {
        console.error('❌ Failed to check biometric availability:', error);
        setBiometricAvailable(false);
        setBiometricEnabled(false);
      }

      // Load permissions status
      await loadPermissionsStatus();
    } catch (error) {
      console.error('❌ Error loading mobile settings:', error);
      toast.error('Failed to load mobile settings');
    } finally {
      setIsLoading(false);
    }
  };

  const loadPermissionsStatus = async () => {
    try {
      const permissionsList = [
        PermissionsManager.permissions.CAMERA,
        PermissionsManager.permissions.PHOTOS,
        PermissionsManager.permissions.LOCATION,
        PermissionsManager.permissions.NOTIFICATIONS,
        PermissionsManager.permissions.MICROPHONE,
        PermissionsManager.permissions.CONTACTS,
        PermissionsManager.permissions.CALENDAR,
        PermissionsManager.permissions.STORAGE,
        PermissionsManager.permissions.BLUETOOTH
      ];

      const permissionsStatus = {};
      for (const permission of permissionsList) {
        const status = await PermissionsManager.getPermissionStatus(permission);
        permissionsStatus[permission] = status;
      }

      setPermissions(permissionsStatus);
    } catch (error) {
      console.error('❌ Error loading permissions status:', error);
    }
  };

  const handleToggleBiometric = async () => {
    try {
      setIsLoading(true);

      if (biometricEnabled) {
        const result = await CapacitorAuthService.disableBiometric();
        if (result.success) {
          setBiometricEnabled(false);
          toast.success('Biometric authentication disabled');
        } else {
          toast.error(result.error || 'Failed to disable biometric authentication');
        }
      } else {
        const result = await CapacitorAuthService.enableBiometric();
        if (result.success) {
          setBiometricEnabled(true);
          toast.success('Biometric authentication enabled');
        } else {
          toast.error(result.error || 'Failed to enable biometric authentication');
        }
      }
    } catch (error) {
      console.error('❌ Error toggling biometric:', error);
      toast.error('Failed to toggle biometric authentication');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestPermission = (permission) => {
    setPermissionDialog({
      isOpen: true,
      permission: permission,
      isRequesting: false
    });
  };

  const handlePermissionAccept = async () => {
    const { permission } = permissionDialog;
    setPermissionDialog(prev => ({ ...prev, isRequesting: true }));

    try {
      let result;
      switch (permission) {
        case PermissionsManager.permissions.CAMERA:
          result = await PermissionsManager.requestCameraPermission();
          break;
        case PermissionsManager.permissions.PHOTOS:
          result = await PermissionsManager.requestPhotosPermission();
          break;
        case PermissionsManager.permissions.LOCATION:
          result = await PermissionsManager.requestLocationPermission();
          break;
        case PermissionsManager.permissions.NOTIFICATIONS:
          result = await PermissionsManager.requestNotificationPermission();
          break;
        case PermissionsManager.permissions.MICROPHONE:
          result = await PermissionsManager.requestMicrophonePermission();
          break;
        case PermissionsManager.permissions.CONTACTS:
          result = await PermissionsManager.requestContactsPermission();
          break;
        case PermissionsManager.permissions.CALENDAR:
          result = await PermissionsManager.requestCalendarPermission();
          break;
        case PermissionsManager.permissions.STORAGE:
          result = await PermissionsManager.requestStoragePermission();
          break;
        case PermissionsManager.permissions.BLUETOOTH:
          result = await PermissionsManager.requestBluetoothPermission();
          break;
        default:
          toast.error('Unknown permission');
          return;
      }

      if (result.success) {
        toast.success('Permission granted');
        await loadPermissionsStatus();
      } else {
        toast.error('Permission denied');
      }
    } catch (error) {
      console.error('❌ Error requesting permission:', error);
      toast.error('Failed to request permission');
    } finally {
      setPermissionDialog({
        isOpen: false,
        permission: null,
        isRequesting: false
      });
    }
  };

  const handlePermissionClose = () => {
    setPermissionDialog({
      isOpen: false,
      permission: null,
      isRequesting: false
    });
  };

  const handleOpenSettings = async () => {
    try {
      await PermissionsManager.openAppSettings();
    } catch (error) {
      console.error('❌ Error opening settings:', error);
      toast.error('Failed to open app settings');
    }
  };

  if (!Capacitor.isNativePlatform()) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-yellow-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <p className="text-yellow-800">Mobile settings are only available on mobile devices.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Mobile Settings</h2>
        <p className="text-gray-600">Manage your mobile app permissions and preferences</p>
      </div>

      {/* Device Information */}
      {deviceInfo && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Device Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Platform:</span>
              <span className="ml-2 text-gray-600">{deviceInfo.platform}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Model:</span>
              <span className="ml-2 text-gray-600">{deviceInfo.model}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">OS:</span>
              <span className="ml-2 text-gray-600">{deviceInfo.operatingSystem} {deviceInfo.osVersion}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">App Version:</span>
              <span className="ml-2 text-gray-600">{deviceInfo.appVersion}</span>
            </div>
          </div>
        </div>
      )}

      {/* Biometric Authentication */}
      {biometricAvailable && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Biometric Authentication</h3>
              <p className="text-sm text-gray-600">
                Use your fingerprint or face to quickly sign in
              </p>
            </div>
            <button
              onClick={handleToggleBiometric}
              disabled={isLoading}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                biometricEnabled ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  biometricEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      )}

      {/* Permissions */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Permissions</h3>
          <button
            onClick={handleOpenSettings}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            Open App Settings
          </button>
        </div>
        
        <div className="space-y-3">
          {Object.entries(permissions).map(([permission, status]) => (
            <div key={permission} className="flex items-center justify-between py-2">
              <div>
                <span className="font-medium text-gray-700 capitalize">
                  {permission.replace('_', ' ')}
                </span>
                <span className={`ml-2 text-sm ${
                  status.status === 'granted' ? 'text-green-600' : 
                  status.status === 'denied' ? 'text-red-600' : 
                  'text-yellow-600'
                }`}>
                  {status.status}
                </span>
              </div>
              {status.status !== 'granted' && status.canRequest && (
                <button
                  onClick={() => handleRequestPermission(permission)}
                  disabled={isLoading}
                  className="text-sm bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  Grant
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Contact Sync Settings */}
      {auth.currentUser && (
        <ContactSyncSettings 
          userId={auth.currentUser.uid}
          appId={process.env.REACT_APP_FIREBASE_APP_ID || 'acctoo-app'}
        />
      )}

      {/* Quick Actions */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Quick Actions</h3>
        <div className="space-y-2">
          <button
            onClick={() => PermissionsManager.requestEssentialPermissions()}
            disabled={isLoading}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md"
          >
            Request Essential Permissions
          </button>
          <button
            onClick={() => PermissionsManager.requestAllPermissions()}
            disabled={isLoading}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md"
          >
            Request All Permissions
          </button>
        </div>
      </div>

      {/* Permission Request Dialog */}
      <PermissionRequestDialog
        isOpen={permissionDialog.isOpen}
        onClose={handlePermissionClose}
        onAccept={handlePermissionAccept}
        permission={permissionDialog.permission}
        isRequesting={permissionDialog.isRequesting}
      />
    </div>
  );
};

export default MobileSettings;

